import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { isPortalConfigured, Portal } from '../core/portal';
import { disposeObject3D, PortalScene, SCENE_FACTORIES } from '../scenes';

/** Half-extents of the volume every scene is authored to stay inside. */
const FRAME_HALF_HEIGHT = 1.95;
const FRAME_HALF_WIDTH = 2.35;

@Component({
  selector: 'app-portal-card',
  templateUrl: './portal-card.html',
  styleUrl: './portal-card.css',
  host: {
    '(pointerenter)': 'onPointerEnter()',
    '(pointerleave)': 'onPointerLeave()',
    '(pointermove)': 'onPointerMove($event)',
    '[style.--accent]': 'portal().accent',
    '[class.is-active]': 'active()',
  },
})
export class PortalCard {
  readonly portal = input.required<Portal>();

  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly active = signal(false);
  protected readonly configured = computed(() => isPortalConfigured(this.portal()));

  private targetHover = 0;
  private hover = 0;
  private targetYaw = 0;
  private targetPitch = 0;
  private yaw = 0;
  private pitch = 0;

  constructor() {
    afterNextRender(() => this.initScene());
  }

  protected onPointerEnter(): void {
    this.targetHover = 1;
    this.active.set(true);
  }

  protected onPointerLeave(): void {
    this.targetHover = 0;
    this.targetYaw = 0;
    this.targetPitch = 0;
    this.active.set(false);
  }

  protected onPointerMove(event: PointerEvent): void {
    const bounds = this.host.nativeElement.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    this.targetYaw = x * 0.9;
    this.targetPitch = y * 0.5;
  }

  private initScene(): void {
    const canvas = this.canvasRef().nativeElement;
    const accent = new THREE.Color(this.portal().accent);

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
      });
    } catch {
      // No WebGL context (blocked, headless or unsupported): keep the card usable without the 3D scene.
      canvas.style.display = 'none';
      return;
    }

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    const scene = new THREE.Scene();

    const pmrem = new THREE.PMREMGenerator(renderer);
    const room = new RoomEnvironment();
    scene.environment = pmrem.fromScene(room, 0.04).texture;
    scene.environmentIntensity = 0.95;
    disposeObject3D(room);
    pmrem.dispose();

    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);

    scene.add(new THREE.HemisphereLight(0xffffff, 0xc2cfea, 2.0));

    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(3.2, 5.2, 4.4);
    scene.add(key);

    const rim = new THREE.PointLight(accent, 16, 22, 2);
    rim.position.set(-3.1, 1.4, 2.6);
    scene.add(rim);

    const fill = new THREE.PointLight(0x9bb0ff, 9, 24, 2);
    fill.position.set(3.0, -2.2, -2.4);
    scene.add(fill);

    const portalScene: PortalScene = SCENE_FACTORIES[this.portal().id](accent);
    scene.add(portalScene.root);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const motionScale = reduceMotion ? 0 : 1;

    const resize = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (!width || !height) {
        return;
      }
      renderer.setSize(width, height, false);
      camera.aspect = width / height;

      // Pull the camera back just enough that the scene's bounds fit whatever shape the card is.
      const halfFov = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
      const distance = Math.max(FRAME_HALF_HEIGHT / halfFov, FRAME_HALF_WIDTH / (halfFov * camera.aspect));
      camera.position.set(0, distance * 0.18, distance);
      camera.lookAt(0, -0.15, 0);
      camera.updateProjectionMatrix();
    };
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const clock = new THREE.Clock();
    let elapsed = reduceMotion ? 1.2 : 0;

    renderer.setAnimationLoop(() => {
      const delta = Math.min(clock.getDelta(), 0.05);
      const ease = Math.min(1, delta * 6);

      elapsed += delta * motionScale;
      this.hover += (this.targetHover - this.hover) * ease;
      this.yaw += (this.targetYaw - this.yaw) * ease;
      this.pitch += (this.targetPitch - this.pitch) * ease;

      portalScene.update(elapsed, this.hover);

      portalScene.root.rotation.y = this.yaw + Math.sin(elapsed * 0.28) * 0.14;
      portalScene.root.rotation.x = this.pitch * 0.6;
      portalScene.root.position.z = this.hover * 0.45;

      rim.intensity = 16 + this.hover * 18;
      renderer.toneMappingExposure = 1.0 + this.hover * 0.12;

      renderer.render(scene, camera);
    });

    this.destroyRef.onDestroy(() => {
      renderer.setAnimationLoop(null);
      observer.disconnect();
      disposeObject3D(scene);
      scene.environment?.dispose();
      renderer.dispose();
    });
  }
}
